import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Package,
  FileText,
  Receipt,
  ShieldCheck,
  Moon,
  Sun,
  User,
  LogOut,
  Menu,
  X,
  CreditCard,
  UserX,
} from 'lucide-react';
import { Button } from './UI/Button';

export const Navbar = () => {
  const { user, isAdmin, isImpersonating, stopImpersonation, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/customers', label: 'Clientes', icon: Users },
    { to: '/projects', label: 'Proyectos', icon: FolderKanban },
    { to: '/inventory', label: 'Inventario', icon: Package },
    { to: '/quotes', label: 'Cotizaciones', icon: FileText },
    { to: '/invoices', label: 'Facturas', icon: Receipt },
  ];

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-slate-950 font-medium px-4 py-2 text-xs flex items-center justify-between shadow-sm sticky top-0 z-50">
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4" />
            <span>
              Modo Impersonación: Navegando como <strong>{user?.name}</strong> ({user?.email})
            </span>
          </div>
          <button
            onClick={stopImpersonation}
            className="px-3 py-1 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold text-xs flex items-center gap-1.5"
          >
            Volver al Panel Admin
          </button>
        </div>
      )}

      {/* Main Navbar */}
      <header className="sticky top-0 z-40 bg-[#08707A] text-white shadow-md backdrop-blur-md bg-opacity-95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Brand Logo & Desktop Nav */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2.5 group">
                <img src="/logo_qi.png" alt="Quinvoice" className="h-9 w-auto transition-transform group-hover:scale-105" />
                <div className="flex flex-col">
                  <span className="font-extrabold text-lg tracking-tight text-white font-sans">
                    QUiNVOICE
                  </span>
                  <span className="text-[10px] text-teal-200 tracking-wider uppercase font-medium -mt-1">
                    Invoice & Inventory
                  </span>
                </div>
              </Link>

              {/* Desktop Links */}
              {user && (
                <nav className="hidden lg:flex items-center gap-1">
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        end={link.to === '/'}
                        className={({ isActive }) => `
                          flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all
                          ${isActive
                            ? 'bg-white/20 text-white shadow-inner font-bold'
                            : 'text-teal-100 hover:bg-white/10 hover:text-white'}
                        `}
                      >
                        <Icon className="w-4 h-4" />
                        {link.label}
                      </NavLink>
                    );
                  })}
                </nav>
              )}
            </div>

            {/* Right: Actions & User Dropdown */}
            {user ? (
              <div className="flex items-center gap-3">
                {/* Admin Link Badge */}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-900/60 hover:bg-teal-900 text-amber-300 text-xs font-bold transition-all border border-amber-400/30 shadow-sm hover:scale-105"
                  >
                    <ShieldCheck className="w-4 h-4 text-amber-400" />
                    <span>Panel Admin</span>
                  </Link>
                )}

                {/* Subscriptions Pill */}
                <Link
                  to="/subscriptions"
                  className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    user.is_subscribed || user.is_admin
                      ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30'
                      : 'bg-amber-400/20 text-amber-200 hover:bg-amber-400/30'
                  }`}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  {user.is_subscribed || user.is_admin ? 'Plan Pro' : 'Plan Free'}
                </Link>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-xl text-teal-100 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Cambiar tema"
                  title={isDark ? 'Modo claro' : 'Modo oscuro'}
                >
                  {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>

                {/* User Menu Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-white/10 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg bg-teal-800 text-white flex items-center justify-center font-bold text-xs border border-white/20">
                      {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="hidden sm:block text-xs text-left">
                      <p className="font-bold text-white leading-tight">{user.name}</p>
                      <p className="text-[11px] text-teal-200 truncate max-w-[120px]">{user.email}</p>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setDropdownOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-2xl shadow-dropdown border border-slate-200/80 dark:border-slate-800 py-2 z-20 animate-fade-in text-slate-800 dark:text-slate-100">
                        <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800">
                          <p className="text-xs font-bold text-slate-900 dark:text-slate-100">{user.name}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                        </div>

                        {isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                          >
                            <ShieldCheck className="w-4 h-4" />
                            Panel Administrativo
                          </Link>
                        )}

                        <Link
                          to="/profile"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          <User className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                          Mi Perfil & Ajustes
                        </Link>

                        <Link
                          to="/subscriptions"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                          Suscripción & Planes
                        </Link>

                        <div className="border-t border-slate-100 dark:border-slate-800 my-1" />

                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            handleLogout();
                          }}
                          className="w-full text-left flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Cerrar Sesión
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 rounded-xl text-teal-100 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Menú móvil"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                    Iniciar Sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="secondary" size="sm" className="bg-white text-[#08707A] hover:bg-teal-50">
                    Registrarse
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && user && (
          <div className="lg:hidden bg-[#065B63] border-t border-white/10 px-4 pt-2 pb-4 space-y-1 animate-fade-in">
            {isAdmin && (
              <NavLink
                to="/admin"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold text-amber-300 bg-teal-900/60"
              >
                <ShieldCheck className="w-4 h-4 text-amber-400" />
                Panel Administrativo
              </NavLink>
            )}

            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold tracking-wide transition-all
                    ${isActive
                      ? 'bg-white/20 text-white font-bold'
                      : 'text-teal-100 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                </NavLink>
              );
            })}

            <div className="pt-2 border-t border-white/10 flex items-center justify-between">
              <Link
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className="text-xs text-teal-100 hover:text-white py-1 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Mi Perfil
              </Link>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="text-xs text-red-200 hover:text-white py-1 flex items-center gap-1.5"
              >
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
